/* eslint-disable consistent-return, no-param-reassign */
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import { jsPDF } from 'jspdf';
import { autoTable, Styles } from 'jspdf-autotable';
import {
  CustomBrick,
  EmptyBrick,
  SurveyHelper,
  IDocOptions,
  DocController,
} from 'survey-pdf';
import {
  ISurvey,
  IQuestion,
  Question,
  QuestionFactory,
} from 'survey-core';

import { regularPdfFont, boldPdfFont } from 'helpers/surveyJs';

// The bundled survey-pdf hits a TypeError in renderParagraph
// (`Cannot read properties of undefined (reading '1')`) whenever its
// splitFragmentsIntoLines emits an empty inner line — which happens when a
// <br> has no text fragment after it within the same paragraph (trailing
// <br>, <br><br> runs, or <br> next to a block boundary). Normalise those
// patterns at survey-pdf's single HTML entry point (`jsPDF.API.fromHTML`)
// so every HTML brick — html questions, comment/file rendering, and
// survey-pdf's internal createHTMLFlat calls — passes through this filter
// once. Healthy HTML matches none of the regexes, so visual output is
// unchanged.
const sanitizeHtmlForRender = (html: unknown): unknown => {
  if (typeof html !== 'string') return html;
  const BLOCK_TAGS = 'div|p|li|h[1-6]|td|th|blockquote|section|article';
  return html
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(new RegExp(`<br\\s*/?>\\s*(?=</(?:${BLOCK_TAGS})>)`, 'gi'), '')
    .replace(new RegExp(`(<(?:${BLOCK_TAGS})\\b[^>]*>)\\s*<br\\s*/?>`, 'gi'), '$1')
    .replace(/^(?:\s|<br\s*\/?>)+/i, '')
    .replace(/(?:\s|<br\s*\/?>)+$/i, '');
};

type FromHTMLFn = (
  html: string,
  x: number,
  y: number,
  settings: unknown,
  callback?: unknown,
  margins?: unknown,
) => unknown;

const jsPdfApi = (jsPDF as unknown as {
  API: { fromHTML?: FromHTMLFn; bdataFromHtmlPatched?: boolean };
}).API;
if (jsPdfApi.fromHTML && !jsPdfApi.bdataFromHtmlPatched) {
  const originalFromHTML = jsPdfApi.fromHTML;
  jsPdfApi.fromHTML = function patchedFromHTML(html, x, y, settings, callback, margins) {
    return originalFromHTML.call(
      this,
      sanitizeHtmlForRender(html) as string,
      x,
      y,
      settings,
      callback,
      margins,
    );
  };
  jsPdfApi.bdataFromHtmlPatched = true;
}

export const surveyPdfOptions: IDocOptions = {
  haveCommercialLicense: true,
  matrixRenderAs: 'list',
  htmlRenderAs: 'standard' as const,
  fontSize: 12,
  fontName: 'Segoe',
  base64Normal: regularPdfFont,
  base64Bold: boldPdfFont,
  useCustomFontInHtml: true,
  textFieldRenderAs: 'multiLine',
  margins: {
    left: 10,
    right: 10,
    top: 18,
    bot: 12,
  },
};

type Element = {
  type: string;
  name: string;
  elements?: Element[];
  templateElements?: Element[];
};

export const mapQuestions = (
  elements: Element[],
): Element[] => elements.reduce<Element[]>((res, el) => {
  if (el.type === 'panel') {
    return [...res, ...mapQuestions(el.elements || [])];
  }
  if (el.type === 'paneldynamic') {
    return [...res, ...mapQuestions(el.templateElements || [])];
  }
  return [...res, el];
}, []);

// Drop authored `type: 'html'` notes (the grey "Complete X in the Appendix"
// / "Onsite:" / "criteria removed" callouts) from the model before
// survey-pdf renders it. Chat-thread entries injected by the backend with
// `includeChat: true` are also `type: 'html'`, but their element names end
// in `.comments` (e.g. `6.8.a.comments`) — keep those. Walks page
// elements, panel.elements, and paneldynamic.templateElements; pages that
// end up empty are removed so the PDF doesn't show bare section headers.
const isChatCommentHtml = (el: any): boolean => typeof el?.name === 'string' && el.name.endsWith('.comments');

const stripHtmlElements = (elements: any[] | undefined): any[] => (elements ?? [])
  .filter((el) => el?.type !== 'html' || isChatCommentHtml(el))
  .map((el) => {
    if (el?.type === 'panel' && el.elements) {
      return { ...el, elements: stripHtmlElements(el.elements) };
    }
    if (el?.type === 'paneldynamic' && el.templateElements) {
      return { ...el, templateElements: stripHtmlElements(el.templateElements) };
    }
    return el;
  });

export const stripHtmlNotes = <M extends { pages: any[] }>(model: M): M => ({
  ...model,
  pages: model.pages
    .map((page) => ({ ...page, elements: stripHtmlElements(page?.elements) }))
    .filter((page) => (page.elements?.length ?? 0) > 0),
});

export const onRenderHeader = (bcqpBurnCenterName: string) => (_: ISurvey, canvas: any): void => {
  const date = new Date();
  canvas.drawText({
    text: `${bcqpBurnCenterName} - ${date.toDateString()} ${date.toLocaleTimeString()}`,
    fontSize: 9,
  });
};

export const onRenderFooter = (_: ISurvey, canvas: any): void => {
  canvas.drawText({
    text: `Page ${canvas.pageNumber} / ${canvas.countPages}`,
    fontSize: 12,
    horizontalAlign: 'center',
  });
};

type RenderOptions = {
  question: Question;
  controller: DocController;
  bricks: any[];
  point: { yTop: number };
  repository: any;
};

const renderTable = (
  options: RenderOptions,
  everyCellSize: { y: number; isPageEnd: boolean }[],
  headers: string[],
  body: string[][],
) => (doc: any, question: any, xLeft: number, yTop: number) => {
  const tableWidth = options.controller.paperWidth
  - (options.controller.margins?.left ?? 0)
  - (options.controller.margins?.right ?? 0);

  const whereEnd: number[] = [];

  const columnStyles: { [key: string]: Partial<Styles> } = {};

  headers.forEach((header, index) => {
    columnStyles[index] = {
      cellWidth: tableWidth / headers.length,
      fillColor: index === 0 && !header ? '#CCC' : 'white',
    };
  });

  autoTable(doc, {
    head: [headers],
    body,
    rowPageBreak: 'avoid',
    showHead: 'firstPage',
    startY: yTop,
    headStyles: {
      textColor: 'black',
      fillColor: '#CCC',
    },
    columnStyles,
    margin: {
      left: options.controller.margins.left,
      right: options.controller.margins.right,
      top: options.controller.margins.top,
      bottom: options.controller.margins.bot,
    },
    styles: {
      lineWidth: 1,
      lineColor: 'black',
    },
    tableWidth,
    didDrawCell: (data: any) => {
      everyCellSize.push({ isPageEnd: false, y: data.cursor.y });
    },
    didDrawPage: (data: any) => {
      whereEnd.push(data.cursor.y);
      everyCellSize.push({ isPageEnd: true, y: data.cursor.y });
    },
  });

  const tableHeight = whereEnd.reduce(
    (res, wE, index) => (index ? res + wE - (options.controller.margins?.top ?? 0) : wE),
    0,
  );

  return {
    xLeft: options.controller.margins.left ?? 0,
    xRight: tableWidth + (options.controller.margins.left ?? 0),
    yTop: options.point.yTop,
    yBot: options.point.yTop + tableHeight,
  };
};

const renderTableBrick = (
  options: RenderOptions,
  everyCellSize: { y: number; isPageEnd: boolean }[],
  tableHeaders: string[],
  tableBody: string[][],
): void => {
  const start = options.point.yTop;
  const tableBrick = new CustomBrick(
    options.question as unknown as IQuestion,
    options.controller,
    renderTable(options, everyCellSize, tableHeaders, tableBody),
  );
  tableBrick.yBot = tableBrick.yTop;
  options.bricks.push(tableBrick);

  const everyRowEnd = [{ isFirstOnPage: true, y: 0 }];
  let sz = 1;
  let lastInPage = false;
  for (let i = tableHeaders.length; i < everyCellSize.length; i += 1) {
    if (everyCellSize[i].y !== everyRowEnd[sz - 1].y) {
      if (lastInPage) {
        everyRowEnd.push({ isFirstOnPage: true, y: everyCellSize[i].y });
        lastInPage = false;
      } else if (everyCellSize[i].isPageEnd) {
        everyRowEnd.push({ isFirstOnPage: false, y: everyCellSize[i].y });
        lastInPage = true;
      } else {
        everyRowEnd.push({ isFirstOnPage: false, y: everyCellSize[i].y });
      }
      sz += 1;
    }
  }

  let last = start;
  for (let i = 0; i < everyRowEnd.length; i += 1) {
    if (!everyRowEnd[i].isFirstOnPage) {
      const rowSize = everyRowEnd[i].y - everyRowEnd[i - 1].y;
      const rect = {
        xLeft: options.controller.margins.left ?? 0,
        xRight: options.controller.paperWidth - (options.controller.margins?.right ?? 0),
        yTop: last,
        yBot: last + rowSize,
      };
      last += rowSize;
      const emptyBrick = new EmptyBrick(rect);
      options.bricks.push(emptyBrick);
    }
  }
};

const formatPdfTableCell = (cellValue: unknown, emptyDisplay: string): string => {
  if (cellValue === null || cellValue === undefined || cellValue === '') {
    return emptyDisplay;
  }
  return String(cellValue);
};

const newHtmlQuestion = (): Question => QuestionFactory.Instance.createQuestion('html', 'html_question');

const fixHTML = (html: string): string => `<div style="font-family: Segoe;">${html.replace('’', "'")}</div>`;

export const onRenderCommentQuestion = ({
  value,
  survey,
  options,
}: {
  value: string;
  survey: ISurvey;
  options: RenderOptions;
}): Promise<void> => {
  const htmlQuestion = newHtmlQuestion();
  const lines = value.split('\n');
  htmlQuestion.html = lines.reduce(
    (res, line) => `${res}<p style="font-family: Segoe;" >${line}</p>`,
    '',
  );

  const flatHtml = options.repository.create(
    survey,
    htmlQuestion,
    options.controller,
    'html',
  );

  const commentBricks = options.bricks[0].unfold();
  const commentBrick = commentBricks.pop();
  const point = SurveyHelper.createPoint(commentBrick, true, true);

  return flatHtml.generateFlats(point).then((htmlBricks: any[]) => {
    options.bricks = commentBricks.concat(htmlBricks);
  });
};

export const onRenderFile = ({
  value,
  survey,
  options,
}: {
  value: { name: string }[];
  survey: ISurvey;
  options: RenderOptions;
}): Promise<void> => {
  const htmlQuestion = newHtmlQuestion();
  htmlQuestion.html = value
    ? value.map((v) => `<p style="font-family: Segoe;">${v.name}</p><br>`).join('')
    : 'No files uploaded';

  const flatHtml = options.repository.create(
    survey,
    htmlQuestion,
    options.controller,
    'html',
  );
  const commentBricks = options.bricks[0].unfold();
  const commentBrick = commentBricks.length > 2 ? commentBricks[2] : commentBricks[1];
  const point = SurveyHelper.createPoint(commentBrick, true, true);

  return flatHtml.generateFlats(point).then((htmlBricks: any[]) => {
    options.bricks = [commentBricks[0]].concat(htmlBricks);
  });
};

export const renderMatrixTable = ({
  name,
  value,
  options,
  modelQuestions,
}: {
  name: string;
  value: any[];
  options: RenderOptions;
  modelQuestions: any[];
}): void => {
  const questionItem = find(modelQuestions, { name });
  const { columns } = questionItem;
  const optionBricks = options.bricks[0].bricks;
  const rowLineIndex = findIndex(optionBricks, (brick) => !((brick as any)).bricks);
  const titleBricks = optionBricks.slice(0, rowLineIndex);
  options.bricks = [...titleBricks];
  const lastTitleBrick = titleBricks[titleBricks.length - 1];
  options.point.yTop = lastTitleBrick.yBot + 10;

  const tableHeaders = columns.map((c: any) => c.name);
  const tableBody = value?.map((rowData) => tableHeaders.map(
    (th: string) => formatPdfTableCell(rowData[th], '-'),
  ));
  const everyCellSize: { y: number; isPageEnd: boolean }[] = [];

  renderTableBrick(options, everyCellSize, tableHeaders, tableBody);
};

export const renderDropTable = ({
  name,
  value,
  options,
  modelQuestions,
}: {
  name: string;
  value: any;
  options: RenderOptions;
  modelQuestions: any[];
}): void => {
  const questionItem = find(modelQuestions, { name });
  if (!questionItem) return;
  const { columns, rows } = questionItem;

  const optionBricks = options.bricks[0].bricks;
  const rowLineIndex = findIndex(optionBricks, (brick) => !(brick as any).bricks);
  options.bricks = rowLineIndex < 3 ? optionBricks.slice(0, rowLineIndex) : [];

  if (options.bricks.length) {
    const lastTitleBrick = options.bricks[options.bricks.length - 1];
    options.point.yTop = lastTitleBrick.yBot + 10;
  }

  const everyCellSize: { y: number; isPageEnd: boolean }[] = [];
  const tableHeaders = columns.map((c: any) => c.name);
  const tableBody = rows.map((r: any) => {
    const rowKey = typeof r === 'string' ? r : r?.value ?? r?.text ?? '';
    const rowLabel = typeof r === 'string' ? r : r?.text ?? r?.value ?? '';
    return [
      rowLabel,
      ...tableHeaders.map((c: string) => formatPdfTableCell(get(value, [rowKey, c]), '')),
    ];
  });

  renderTableBrick(options, everyCellSize, ['', ...tableHeaders], tableBody);
};

const flattenBricks = (bricks: any[]): any[] => bricks.flatMap(
  (b) => (b && typeof b.unfold === 'function' ? b.unfold() : [b]),
);

const appendAppendixHint = (survey: ISurvey, options: RenderOptions): Promise<void> => {
  const hintQuestion = newHtmlQuestion();
  hintQuestion.html = '<i>See Table in Appendix</i>';
  const flatHint = options.repository.create(
    survey,
    hintQuestion,
    options.controller,
    'html',
  );
  const allBricks = flattenBricks(options.bricks);
  const anchorBrick = allBricks.reduce(
    (acc, b) => ((b?.yBot ?? 0) > (acc?.yBot ?? 0) ? b : acc),
    allBricks[0],
  );
  const point = SurveyHelper.createPoint(anchorBrick, true, false);
  return flatHint.generateFlats(point).then((hintBricks: any[]) => {
    options.bricks = options.bricks.concat(hintBricks);
  });
};

export const onRenderQuestion = (
  modelQuestions: any[],
  { overrider = {} }: { overrider?: any },
) => (survey: ISurvey, options: RenderOptions): Promise<void> | void => {
  const {
    value,
    html,
    name,
    getType,
  } = options.question;
  const questionType = getType();
  const isInAppendixSection = options?.question?.page?.name === '21_2';
  const isAppendix = overrider?.appendixQuestions?.includes(name) && !isInAppendixSection;

  const renderResult: Promise<void> | void = (() => {
    if (questionType === 'html' && html?.length) {
      const htmlQuestion = newHtmlQuestion();
      htmlQuestion.html = fixHTML(html);

      const flatHtml = options.repository.create(
        survey,
        htmlQuestion,
        options.controller,
        'html',
      );
      const commentBricks = options.bricks[0].unfold();
      const commentBrick = commentBricks.pop();
      const point = SurveyHelper.createPoint(commentBrick, true, true);

      return flatHtml.generateFlats(point).then((htmlBricks: any[]) => {
        options.bricks = htmlBricks;
      });
    }
    if (questionType === 'comment' && value?.length) {
      return onRenderCommentQuestion({ value, survey, options });
    }
    if (questionType === 'file') {
      return onRenderFile({ value, survey, options });
    }
    if (questionType === 'matrixdynamic' && overrider?.matrixDynamic2Table?.includes(name)) {
      return renderMatrixTable({
        name,
        value,
        options,
        modelQuestions,
      });
    }
    if (questionType === 'matrixdropdown' && overrider?.matrixDropdown2Table?.includes(name)) {
      return renderDropTable({
        name,
        value,
        options,
        modelQuestions,
      });
    }
    return undefined;
  })();

  if (!isAppendix) {
    return renderResult;
  }

  if (renderResult instanceof Promise) {
    return renderResult.then(() => appendAppendixHint(survey, options));
  }
  return appendAppendixHint(survey, options);
};
